// Copyright (c) 2025 MintJams Inc. Licensed under MIT License.

import { VComponentRegistry } from './components/VComponentRegistry';
import { VDirectiveParserRegistry } from './directives/VDirectiveParserRegistry';
import { VStandardDirectiveParser } from './directives/VStandardDirectiveParser';
import { VApplication } from './VApplication';

// Re-export types for external use
export { VWatcher } from './VWatcher';
export type { WatchCallback, WatcherOptions, WatcherDefinition, WatcherDictionary } from './VWatcherOptions';

/**
 * The main entry point for the virtual DOM library.
 */
export class VDOM {
    /**
     * The registry for components.
     */
    static #componentRegistry = new VComponentRegistry();

    /**
     * The registry for directive parsers.
     */
    static #directiveParserRegistry = new VDirectiveParserRegistry();
    static {
        // Register standard directive parser
        this.#directiveParserRegistry.register(new VStandardDirectiveParser());
    }

    /**
     * Gets the component registry.
     * @return {VComponentRegistry} The component registry.
     * @deprecated This method is deprecated and will be removed in a future release. Please use the new component registration system instead.
     */
    static get componentRegistry(): VComponentRegistry {
        return this.#componentRegistry;
    }

    /**
     * Gets the directive parser registry.
     * @return {VDirectiveParserRegistry} The directive parser registry.
     */
    static get directiveParserRegistry(): VDirectiveParserRegistry {
        return this.#directiveParserRegistry;
    }

    /**
     * Checks if the current environment supports the required features.
     * @return {boolean} True if supported, false otherwise.
     */
    static get isSupported(): boolean {
        try {
            // Check for native support of required APIs
            if (typeof structuredClone !== 'function') return false;

            // if (typeof crypto.randomUUID !== 'function') return false;
            // if (!Array.prototype.at) return false;

            // Check for DOM support
            return typeof document !== 'undefined' && typeof document.createElement === 'function';
        } catch {
            return false;
        }
    }

    /**
     * Creates a virtual application instance.
     * @param options The options for the virtual application.
     * @returns The created virtual application instance.
     */
    static createApp(options: any): VApplication {
        return new VApplication({
            options,
            directiveParserRegistry: this.#directiveParserRegistry,
            componentRegistry: this.#componentRegistry
        });
    }
}
